<template>
  <div class="min-h-40">
    <TableLight>
      <thead>
        <tr>
          <TableH :label="t('Reviews.product.campaign.table.startDate')" />
          <TableH :label="t('Reviews.product.campaign.table.endDate')" />
          <TableH :label="t('Reviews.product.campaign.table.campaign')" />
          <TableH :label="t('Reviews.product.campaign.table.status')" />
          <TableH :label="t('Reviews.product.campaign.table.incentive')" />
          <TableH :label="t('Reviews.product.campaign.table.usedBudget')" />
          <TableH :label="t('Reviews.product.campaign.table.remainingBudget')" />
          <TableH :label="t('Reviews.product.campaign.table.allReviews')" />
          <TableH :label="t('Reviews.product.campaign.table.reviews')" />
        </tr>
      </thead>
      <tbody>
        <tr v-if="campaigns.length === 0 && !loading">
          <td colspan="9">
            <div class="py-12 text-center">
              <h3>{{ t('Reviews.product.campaign.emptyResultTitle') }}</h3>
              <div>{{ t('Reviews.product.campaign.emptyResultText') }}</div>
              <RouterLink
                class="btn-outline-color-xs mt-8"
                data-testid="reviews-campaigns-new"
                :to="{ name: ReviewsRouteName.ReviewsProductCampaignNew, params: { productId: productId } }"
                @click="track('campaign_create_start', { surface: 'editor', entity_type: 'tool', entity_id: productId })"
              >
                {{ t('Reviews.product.action.newCampaign') }} <PlusIcon class="lucide-icon-s" />
              </RouterLink>
            </div>
          </td>
        </tr>
        <tr
          v-for="(campaign, idx) in campaigns"
          :key="campaign.id || campaign.title || idx"
          class="cursor-pointer hover:bg-surface-elevation-2"
          :data-testid="`reviews-campaign-row-${campaign.id || idx}`"
          @click="openCampaignOverview(campaign.id)"
        >
          <td class="text-left">{{ formatDate(campaign.validFrom) }}</td>
          <td class="text-left">{{ formatDate(campaign.validTo) }}</td>
          <td class="text-left">{{ campaign.title }}</td>
          <td class="text-left">
            <CampaignStatusBadge v-if="campaign.status" :status="campaign.status" />
          </td>
          <td class="text-left" data-testid="campaign-incentive">{{ formatIncentiveValue(campaign) }}</td>
          <td class="text-left" data-testid="campaign-used-budget">{{ formatUsedBudget(campaign) }}</td>
          <td class="text-left" data-testid="campaign-remaining-budget">{{ formatRemainingBudget(campaign) }}</td>
          <td class="text-left" data-testid="campaign-all-reviews-count">{{ campaign.associatedReviewsCount ?? 0 }}</td>
          <td class="text-left" data-testid="campaign-reviews-count">{{ formatReviewsCount(campaign) }}</td>
        </tr>
      </tbody>
    </TableLight>
    <div v-if="loading" class="mt-6 flex justify-center">
      <CircleSpinner />
    </div>
  </div>
</template>

<script setup lang="ts">
import { CircleSpinner, useCurrencyFormatter } from '@ramp106/omrjs-core-ui'
import { PlusIcon } from '@ramp106/omrjs-core-ui/icons'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { isNumber } from 'lodash-es'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import type { IncentivizationCampaign } from '@/gql/reviews'
import { ReviewsRouteName } from '@/router/types'
import { TableH, TableLight } from '@/ui/TableLight'

import CampaignStatusBadge from './CampaignStatusBadge.vue'
import { useFormatTableDate } from './useFormatTableDate'

const props = defineProps<{
  productId: string
  campaigns: IncentivizationCampaign[]
  remainingGlobalBudget: number
  loading?: boolean
}>()

const { t } = useI18n()
const router = useRouter()

const formatDate = useFormatTableDate()
const { formatCurrency } = useCurrencyFormatter()

function campaignHasIncentive(campaign: IncentivizationCampaign) {
  return isNumber(campaign.amount)
}

function formatIncentiveValue(campaign: IncentivizationCampaign) {
  if (campaignHasIncentive(campaign)) {
    return formatCurrency(campaign.amount || 0)
  }
  return '---'
}

function formatReviewsCount(campaign: IncentivizationCampaign) {
  if (typeof campaign.incentivizationsLimit === 'number') {
    return `${campaign.wonIncentivizationsCount}/${campaign.incentivizationsLimit}`
  }
  return campaign.wonIncentivizationsCount || 0
}

function formatUsedBudget(campaign: IncentivizationCampaign) {
  if (!campaignHasIncentive(campaign)) {
    return '---'
  }
  return formatCurrency((campaign.wonIncentivizationsCount || 0) * (campaign.amount || 0))
}

function formatRemainingBudget(campaign: IncentivizationCampaign) {
  if (!campaignHasIncentive(campaign)) {
    return '---'
  }
  if (!isNumber(campaign.incentivizationsLimit)) {
    return formatCurrency(props.remainingGlobalBudget)
  }
  const remainingIncentivizations = campaign.incentivizationsLimit - (campaign.wonIncentivizationsCount || 0)
  const remainingBudget = remainingIncentivizations * (campaign.amount || 0)
  return formatCurrency(remainingBudget)
}

function openCampaignOverview(campaignId: string | null | undefined) {
  if (!campaignId) return
  router.push({
    name: ReviewsRouteName.ReviewsProductCampaignOverview,
    params: {
      productId: props.productId,
      campaignId,
    },
  })
}
</script>
